/**
 * SPEC-annotation-review-mode (Phase 3) — pure helpers shared by the
 * qa-detail-v3 review-mode UI and the /admin/annotation-review queue page.
 *
 * Why these live in their own module: review mode has three pieces of
 * intrinsically client-side behavior (verdict-UI swap, auto-save on
 * navigation, keyboard shortcuts) that the spec lists as U-tests. The repo
 * has no client-side test runner. The skill's chain-coverage rule for
 * client-only behavior is to extract a pure helper and unit-test it from
 * a vitest server test (Node-only, no DOM, no React). Putting the helpers
 * here keeps the unit-test surface tiny and makes the JSX read like:
 *
 *   if (isReviewMode(searchString)) <ReviewFooter ... /> else <QaFooter />
 *
 * — which the subagent verification step audits to confirm the component
 * actually defers to the helper.
 *
 * No DOM access, no React imports, no `window` reads. Pure functions only.
 */

import type { QaRegion } from "@shared/qa-regions";

export type ReviewStatus = "pending" | "verified" | "discarded";

/** localStorage key used by both the queue page and qa-detail-v3 to store
 * the most recent delivery the user touched in review mode. Exported as a
 * constant so both pages reference the same value.
 */
export const RESUME_LOCALSTORAGE_KEY = "annotation-review:last-delivery-id";

// ─────────────────────────────────────────────────────────────────────────────
// U3 — review-mode flag.
// The spec lists `?review=1` as the activation token; do an exact match so
// `?review=true`, `?review=0`, and the empty-string variant are all false.
// ─────────────────────────────────────────────────────────────────────────────

/** True iff `search` (with or without leading `?`) contains `review=1`. */
export function isReviewMode(search: string): boolean {
  if (!search) return false;
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return params.get("review") === "1";
}

// ─────────────────────────────────────────────────────────────────────────────
// Filter wire format — the queue page (/admin/annotation-review) and the
// neighbors endpoint share these query params. Keep names in sync with
// server/annotation-review-queue-handler.ts and
// server/annotation-review-neighbors-handler.ts.
// ─────────────────────────────────────────────────────────────────────────────

export type ReviewStatusFilter = ReviewStatus | "any";

export interface ReviewQueryFilters {
  /** Selected annotation classes; empty array means "let the server apply default = cookunity_bag". */
  classes: string[];
  reviewStatus: ReviewStatusFilter;
  dzType: string;
  dateFrom: string;
  dateTo: string;
  /**
   * SPEC-annotation-review-seed-sources Phase 2 — multi-select on
   * region_review_seed_source. Empty array = no seed-source filter (server
   * applies the cookunity_bag class default). When present, the server
   * passes classes:[] to storage so batch rows with no annotations come back.
   * Detail page forwards this on /api/admin/annotation-review/neighbors so
   * Prev/Next walks the same set as the queue page.
   */
  seedSources: string[];
}

const ALLOWED_REVIEW_STATUSES: readonly ReviewStatusFilter[] = [
  "pending",
  "verified",
  "discarded",
  "any",
] as const;

export function parseReviewQueryFilters(search: string): ReviewQueryFilters {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const classRaw = params.get("class");
  const classes = classRaw
    ? classRaw.split(",").map((s) => s.trim()).filter(Boolean)
    : [];
  const reviewStatusRaw = params.get("review_status") ?? "pending";
  const reviewStatus = (ALLOWED_REVIEW_STATUSES as readonly string[]).includes(reviewStatusRaw)
    ? (reviewStatusRaw as ReviewStatusFilter)
    : "pending";
  // SPEC-annotation-review-seed-sources — same comma-split / whitespace-strip
  // shape as the queue page. Without this the detail page calls the
  // neighbors API with no seed_source, the server defaults to
  // class=['cookunity_bag'], and Prev/Next on a batch row dies with
  // "Delivery not in active filter (0 match)".
  const seedSourceRaw = params.get("seed_source");
  const seedSources = seedSourceRaw
    ? seedSourceRaw.split(",").map((s) => s.trim()).filter(Boolean)
    : [];
  return {
    classes,
    reviewStatus,
    dzType: params.get("dz_type") ?? "",
    dateFrom: params.get("date_from") ?? "",
    dateTo: params.get("date_to") ?? "",
    seedSources,
  };
}

export function buildReviewQueryString(f: ReviewQueryFilters): string {
  const p = new URLSearchParams();
  if (f.classes.length > 0) p.set("class", f.classes.join(","));
  p.set("review_status", f.reviewStatus);
  if (f.dzType) p.set("dz_type", f.dzType);
  if (f.dateFrom) p.set("date_from", f.dateFrom);
  if (f.dateTo) p.set("date_to", f.dateTo);
  // SPEC-annotation-review-seed-sources — emit when non-empty so the neighbors
  // API gets the same filter the queue page used. Default URL (no
  // seed_source) keeps emitting nothing, preserving the cookunity_bag
  // default for the original 1,657 set.
  if (f.seedSources.length > 0) p.set("seed_source", f.seedSources.join(","));
  return p.toString();
}

// ─────────────────────────────────────────────────────────────────────────────
// U6 — buildReviewSavePayload: canonical body for the Phase 1 PATCH.
// The spec's hard isolation guarantee (Phase 1 A2/A3) is that the endpoint
// rejects any extra top-level keys. Building the body here in one place
// means the page can never accidentally include an extra field.
// ─────────────────────────────────────────────────────────────────────────────

export interface ReviewSavePayload {
  qa_region_annotations: QaRegion[];
  region_review_status: ReviewStatus;
}

/** Accepts either a flat array of regions or the by-photo-index record the
 * v3 page keeps in state, and returns the strict two-key body the Phase 1
 * PATCH endpoint accepts. No other keys, ever — that's the isolation
 * guarantee. */
export function buildReviewSavePayload(
  regions: QaRegion[] | Record<number, QaRegion[]>,
  status: ReviewStatus,
): ReviewSavePayload {
  const flat: QaRegion[] = Array.isArray(regions)
    ? regions
    : Object.keys(regions)
        .map((k) => Number(k))
        .sort((a, b) => a - b)
        .flatMap((k) => regions[k] ?? []);
  return {
    qa_region_annotations: flat,
    region_review_status: status,
  };
}

/** Server endpoint for Phase 1 saves. The page must always go through
 * THIS path while review mode is active — never the legacy
 * /api/qa/deliveries/:id/regions endpoint. */
export function reviewSaveUrl(deliveryId: string): string {
  return `/api/qa-deliveries/${deliveryId}/region-annotations`;
}

/** Server endpoint that returns prev/next/position/total for a delivery
 * within the current filter set. Filters share the queue page's wire
 * format. */
export function reviewNeighborsUrl(deliveryId: string, filters: ReviewQueryFilters): string {
  const params = new URLSearchParams(buildReviewQueryString(filters));
  params.set("id", deliveryId);
  return `/api/admin/annotation-review/neighbors?${params.toString()}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// U7 — keyboard shortcut map.
// The component owns one keydown listener that delegates to this function.
// Returning null means "let the event pass through unchanged".
// ─────────────────────────────────────────────────────────────────────────────

export type ReviewAction = "next" | "prev" | "verified-advance" | "discarded-advance";

interface KeyEventLike {
  key: string;
  target: { tagName?: string; isContentEditable?: boolean } | null;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
}

/** Maps a KeyboardEvent-shape to a review-mode action, or null if the event
 * should pass through. Ignores keys pressed inside form fields and any
 * event with a modifier held. */
export function keyToReviewAction(ev: KeyEventLike): ReviewAction | null {
  if (ev.ctrlKey || ev.metaKey || ev.altKey) return null;

  const tag = (ev.target?.tagName ?? "").toUpperCase();
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return null;
  if (ev.target?.isContentEditable) return null;

  switch (ev.key) {
    case "j":
    case "ArrowRight":
      return "next";
    case "k":
    case "ArrowLeft":
      return "prev";
    case "v":
      return "verified-advance";
    case "d":
      return "discarded-advance";
    default:
      return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// SPEC-annotation-review-concurrency Phase 5 — review:update event mapping +
// PATCH-409 parsing helpers. Pure functions, server-vitest testable.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  ReviewClaimConflictBody,
  ReviewUpdateEvent,
} from "@shared/annotation-review-claims";

/** Parses an unknown 409 response body into the typed conflict shape, or
 * null if the body doesn't match the contract. Keeps the page free of
 * `as any` casts and gives the take-over banner a single source of truth
 * for what fields are guaranteed present. */
export function parseClaimConflictBody(body: unknown): ReviewClaimConflictBody | null {
  if (!body || typeof body !== "object") return null;
  const b = body as Record<string, unknown>;
  if (typeof b.claimedBy !== "string" || b.claimedBy.length === 0) return null;
  const claimedByName =
    typeof b.claimedByName === "string" ? b.claimedByName : null;
  const claimedAt = typeof b.claimedAt === "string" ? b.claimedAt : null;
  return {
    claimedBy: b.claimedBy,
    claimedByName,
    claimedAt,
  };
}

/** Should the displaced-reviewer take-over toast fire on this event?
 *
 * Toast fires iff every condition holds:
 *   - the event is for the delivery the user is currently viewing,
 *   - the event is a `claimed` event (not a release / verdict_changed),
 *   - the reason is `took_over` (so quiet first-claim broadcasts don't toast),
 *   - the new claimer is NOT the current user (so a self-take-over after a
 *     reconnect doesn't surprise me with a "Claimed by Sarah" toast).
 *
 * The qa-detail-v3 page subscribes to all review:update events but only
 * routes to the toast pipeline when this returns true.
 */
export function shouldShowTakeOverToast(
  event: ReviewUpdateEvent,
  currentDeliveryId: string | null | undefined,
  currentUserId: string | null | undefined,
): boolean {
  if (!currentDeliveryId || event.deliveryId !== currentDeliveryId) return false;
  if (event.type !== "claimed") return false;
  if (event.reason !== "took_over") return false;
  if (!currentUserId) return false;
  if (!event.claimerId || event.claimerId === currentUserId) return false;
  return true;
}

/** Should the page swap into the read-only "claimed by another reviewer"
 * banner state on this event? Triggers on the same conditions as
 * shouldShowTakeOverToast PLUS plain non-take-over claim events for the
 * currently-viewed delivery (the "I navigated to a delivery Sunny is
 * already on" path). */
export function shouldEnterReadOnlyBanner(
  event: ReviewUpdateEvent,
  currentDeliveryId: string | null | undefined,
  currentUserId: string | null | undefined,
): boolean {
  if (!currentDeliveryId || event.deliveryId !== currentDeliveryId) return false;
  if (event.type !== "claimed") return false;
  if (!currentUserId) return false;
  if (!event.claimerId || event.claimerId === currentUserId) return false;
  return true;
}

/** Should the toast for "the verdict was changed by another reviewer" fire?
 * The qa-detail-v3 page uses this to render a "X has been verified by
 * Sarah; back to queue?" prompt in real time. */
export function shouldShowVerdictChangedToast(
  event: ReviewUpdateEvent,
  currentDeliveryId: string | null | undefined,
  currentUserId: string | null | undefined,
): boolean {
  if (!currentDeliveryId || event.deliveryId !== currentDeliveryId) return false;
  if (event.type !== "verdict_changed") return false;
  // If we're the actor (we just saved), don't toast ourselves — the save
  // confirmation flow already gives us feedback.
  if (currentUserId && event.claimerId === currentUserId) return false;
  return true;
}

/**
 * SPEC-annotation-review-concurrency Phase 6 — formatted "time left until
 * the claim auto-expires" string for the queue's "Show in-progress" toggle
 * row chips. Returns "Xm left" for sub-hour windows or "expired" once past
 * TTL. Pure function; takes `now` as a parameter so tests can pin it.
 */
export function formatClaimTimeLeft(
  claimedAtIso: string,
  ttlMs: number,
  now: number = Date.now(),
): string {
  const claimedAt = Date.parse(claimedAtIso);
  if (!Number.isFinite(claimedAt)) return "—";
  const expiresAt = claimedAt + ttlMs;
  const remainingMs = expiresAt - now;
  if (remainingMs <= 0) return "expired";
  const minutes = Math.ceil(remainingMs / 60_000);
  return `${minutes}m left`;
}
